import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import './TemplatePreviewModal.css';

const TemplatePreviewModal = ({ isOpen, onClose, template }) => {
  const [activeTab, setActiveTab] = useState('preview'); // 'preview' | 'metaJson'

  if (!isOpen || !template) return null;

  const renderHighlightedBody = () => {
    let body = template.bodyText || '';
    if (!body) return <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>No body text provided</span>;

    // Highlight variables like {{1}}, {{CustomerName}}, etc.
    const parts = body.split(/(\{\{[^}]+\}\})/g);
    return parts.map((part, index) => {
      if (part.startsWith('{{') && part.endsWith('}}')) {
        return (
          <span
            key={index}
            style={{
              background: '#e0f2fe',
              color: '#0369a1',
              padding: '1px 5px',
              borderRadius: '4px',
              fontWeight: 700,
              fontFamily: 'monospace',
              fontSize: '13px',
              border: '1px solid #bae6fd'
            }}
          >
            {part}
          </span>
        );
      }
      return part;
    });
  };

  const getStatusBadge = (status) => {
    const st = (status || 'DRAFT').toUpperCase();
    if (st === 'APPROVED') {
      return (
        <span style={{ fontSize: '12px', fontWeight: 700, padding: '4px 10px', borderRadius: '16px', background: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <Icon icon="mdi:check-circle" /> Approved
        </span>
      );
    } else if (st === 'PENDING') {
      return (
        <span style={{ fontSize: '12px', fontWeight: 700, padding: '4px 10px', borderRadius: '16px', background: '#fefce8', color: '#a16207', border: '1px solid #fef08a', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <Icon icon="mdi:clock-outline" /> Pending
        </span>
      );
    } else if (st === 'REJECTED') {
      return (
        <span style={{ fontSize: '12px', fontWeight: 700, padding: '4px 10px', borderRadius: '16px', background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <Icon icon="mdi:alert-circle" /> Rejected
        </span>
      );
    } else if (st === 'PAUSED') {
      return (
        <span style={{ fontSize: '12px', fontWeight: 700, padding: '4px 10px', borderRadius: '16px', background: '#ffedd5', color: '#c2410c', border: '1px solid #fed7aa', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <Icon icon="mdi:pause-circle" /> Paused
        </span>
      );
    } else {
      return (
        <span style={{ fontSize: '12px', fontWeight: 700, padding: '4px 10px', borderRadius: '16px', background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <Icon icon="mdi:file-document-outline" /> {st}
        </span>
      );
    }
  };

  const getQualityBadge = (score) => {
    const sc = (score || 'UNKNOWN').toUpperCase();
    if (sc === 'GREEN' || sc === 'HIGH') {
      return <span style={{ color: '#16a34a', fontWeight: 700 }}>🟢 High Quality</span>;
    } else if (sc === 'YELLOW' || sc === 'MEDIUM') {
      return <span style={{ color: '#d97706', fontWeight: 700 }}>🟡 Medium Quality</span>;
    } else if (sc === 'RED' || sc === 'LOW') {
      return <span style={{ color: '#dc2626', fontWeight: 700 }}>🔴 Low Quality</span>;
    }
    return <span style={{ color: '#64748b' }}>⚪ Unknown</span>;
  };

  return (
    <div className="tmpl-preview-overlay" onClick={onClose}>
      <div className="tmpl-preview-modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '640px', width: '92%' }}>
        {/* Header */}
        <div className="tmpl-preview-modal-header" style={{ padding: '16px 24px', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
              {template.templateName}
            </h3>
            {getStatusBadge(template.metaStatus)}
          </div>
          <button
            style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#64748b' }}
            onClick={onClose}
          >
            <Icon icon="mdi:close" />
          </button>
        </div>

        {/* Navigation Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', padding: '0 24px' }}>
          <button
            style={{
              padding: '12px 18px',
              border: 'none',
              background: 'none',
              borderBottom: activeTab === 'preview' ? '2.5px solid #25d366' : '2.5px solid transparent',
              color: activeTab === 'preview' ? '#075e54' : '#64748b',
              fontWeight: activeTab === 'preview' ? 800 : 600,
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onClick={() => setActiveTab('preview')}
          >
            <Icon icon="mdi:whatsapp" /> WhatsApp Live Preview
          </button>
          <button
            style={{
              padding: '12px 18px',
              border: 'none',
              background: 'none',
              borderBottom: activeTab === 'metaJson' ? '2.5px solid #25d366' : '2.5px solid transparent',
              color: activeTab === 'metaJson' ? '#075e54' : '#64748b',
              fontWeight: activeTab === 'metaJson' ? 800 : 600,
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onClick={() => setActiveTab('metaJson')}
          >
            <Icon icon="mdi:code-json" /> Meta API Payload & Details
          </button>
        </div>

        {/* Modal Body */}
        <div className="tmpl-preview-modal-body" style={{ padding: '20px 24px', maxHeight: '70vh', overflowY: 'auto' }}>
          {activeTab === 'preview' ? (
            <div>
              {/* Metadata strip */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px', marginBottom: '18px', padding: '12px', background: '#f1f5f9', borderRadius: '10px', fontSize: '12.5px' }}>
                <div><strong>Category:</strong> {template.metaCategory || template.category}</div>
                <div><strong>Language:</strong> {template.language || 'en_US'}</div>
                <div><strong>Quality Rating:</strong> {getQualityBadge(template.qualityRating)}</div>
                <div><strong>Header Format:</strong> {template.headerType || template.mediaType || 'NONE'}</div>
              </div>

              {/* Rejection Alert if REJECTED */}
              {template.metaStatus === 'REJECTED' && (
                <div style={{ padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', marginBottom: '18px', color: '#991b1b', fontSize: '13px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <Icon icon="mdi:alert" style={{ fontSize: '20px', flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <strong>Rejection Reason from Meta:</strong>
                    <div style={{ marginTop: '2px' }}>{template.rejectionReason || 'No specific rejection reason provided by Meta.'}</div>
                  </div>
                </div>
              )}

              {/* WhatsApp Device Mockup */}
              <div className="preview-phone-mockup" style={{ margin: '0 auto', maxWidth: '380px' }}>
                <div className="preview-phone-screen">
                  {/* WhatsApp App Bar */}
                  <div className="preview-app-bar" style={{ background: '#075e54', color: '#ffffff', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Icon icon="mdi:whatsapp" style={{ fontSize: '22px' }} />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '14px', lineHeight: 1.2 }}>
                        {template.storeId?.storeName || 'Official Business'}
                      </div>
                      <div style={{ fontSize: '10.5px', opacity: 0.85 }}>WhatsApp Business Account</div>
                    </div>
                  </div>

                  {/* Chat Area */}
                  <div className="preview-chat-body" style={{ background: '#efeae2', padding: '14px 12px' }}>
                    <div className="preview-chat-bubble" style={{ background: '#ffffff', borderRadius: '8px', padding: '12px 14px', boxShadow: '0 1px 2px rgba(0,0,0,0.12)', borderTopLeftRadius: '2px' }}>
                      
                      {/* Media/Header Attachment */}
                      {['IMAGE', 'VIDEO', 'DOCUMENT', 'Image', 'PDF', 'Video'].includes(template.headerType || template.mediaType) && (
                        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '12px', marginBottom: '10px', textAlign: 'center', color: '#64748b' }}>
                          <Icon
                            icon={
                              (template.headerType || template.mediaType).toLowerCase().includes('image')
                                ? 'mdi:file-image'
                                : (template.headerType || template.mediaType).toLowerCase().includes('video')
                                ? 'mdi:file-video'
                                : 'mdi:file-document'
                            }
                            style={{ fontSize: '32px', color: '#075e54' }}
                          />
                          <div style={{ fontSize: '12px', fontWeight: 600, marginTop: '4px' }}>
                            [{template.headerType || template.mediaType} Attachment Header]
                          </div>
                        </div>
                      )}

                      {/* Header Text */}
                      {template.headerText && (
                        <div style={{ fontWeight: 800, marginBottom: '8px', color: '#0f172a', fontSize: '14.5px' }}>
                          {template.headerText}
                        </div>
                      )}

                      {/* Body Text */}
                      <div style={{ fontSize: '13.5px', color: '#1e293b', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                        {renderHighlightedBody()}
                      </div>

                      {/* Footer Text */}
                      {template.footerText && (
                        <div style={{ fontSize: '11px', color: '#64748b', marginTop: '10px', paddingTop: '6px', borderTop: '1px dashed #cbd5e1' }}>
                          {template.footerText}
                        </div>
                      )}

                      {/* Message Timestamp */}
                      <div style={{ textAlign: 'right', fontSize: '10px', color: '#94a3b8', marginTop: '4px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                        {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        <Icon icon="mdi:check-all" style={{ color: '#34b7f1', fontSize: '14px' }} />
                      </div>

                      {/* Action Buttons */}
                      {template.buttons && template.buttons.length > 0 && (
                        <div style={{ marginTop: '10px', borderTop: '1px solid #f1f5f9', paddingTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {template.buttons.map((btn, idx) => (
                            <div
                              key={idx}
                              style={{
                                background: '#ffffff',
                                border: '1px solid #e2e8f0',
                                color: '#00a884',
                                fontWeight: 700,
                                fontSize: '13px',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                textAlign: 'center',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px'
                              }}
                            >
                              {btn.type === 'PHONE_NUMBER' && <Icon icon="mdi:phone" />}
                              {btn.type === 'URL' && <Icon icon="mdi:open-in-new" />}
                              {btn.type === 'QUICK_REPLY' && <Icon icon="mdi:reply" />}
                              {btn.type === 'COPY_CODE' && <Icon icon="mdi:content-copy" />}
                              {btn.text || 'Action Button'}
                            </div>
                          ))}
                        </div>
                      )}

                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Meta Raw JSON Tab */
            <div>
              <div style={{ marginBottom: '12px', fontSize: '13px', color: '#475569' }}>
                Below is the detailed record and raw Meta Graph API response for this template:
              </div>
              <div style={{ marginBottom: '12px', background: '#f8fafc', padding: '12px', borderRadius: '8px', fontSize: '12.5px' }}>
                <div><strong>Meta Template ID:</strong> {template.metaTemplateId || 'Not yet assigned by Meta'}</div>
                <div><strong>Template Name:</strong> {template.templateName}</div>
                <div><strong>Meta Category:</strong> {template.metaCategory}</div>
                <div><strong>Language Code:</strong> {template.language}</div>
                <div><strong>Last Synced Time:</strong> {template.lastSyncTime ? new Date(template.lastSyncTime).toLocaleString('en-IN') : 'Never'}</div>
              </div>
              <pre
                style={{
                  background: '#0f172a',
                  color: '#38bdf8',
                  padding: '16px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontFamily: 'monospace',
                  overflowX: 'auto',
                  maxHeight: '320px'
                }}
              >
                {JSON.stringify(template.metaResponse || template, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', background: '#f8fafc' }}>
          <button
            style={{
              padding: '8px 20px',
              borderRadius: '8px',
              border: 'none',
              background: '#3C0008',
              color: '#ffffff',
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: '13px'
            }}
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplatePreviewModal;
